import React from "react";

import {
  isValidField as isValidFieldAPI,
  formatDateAPI,
  formatNumericAPI,
} from "../../../../Utils/WatchlistSummary/validation";

// 🎯 Types and Interfaces
interface ExecutiveChange {
  name?: string;
  title?: string;
  type?: string;
  description?: string;
  effectiveDate?: string;
}

interface OrganizationalChange {
  title?: string;
  description?: string;
  impact?: string;
  effectiveDate?: string;
}

interface CulturalInitiative {
  title?: string;
  description?: string;
  status?: string;
}

interface Executive {
  name?: string;
  title?: string;
  role?: string;
  briefBio?: string;
  tenure?: string;
  source?: string;
}

// 🎯 Organization Relevance Section Component
export const OrganizationRelevanceSection: React.FC<{
  sentiment?: string;
  relevance?: string;
  keyConnections?: string;
  strategicImplications?: string;
  competitiveIntelligence?: string;
  partnershipOpportunities?: string;
  riskFactors?: string;
  organizationName?: string;
}> = ({
  sentiment,
  relevance,
  keyConnections,
  strategicImplications,
  competitiveIntelligence,
  partnershipOpportunities,
  riskFactors,
  organizationName,
}) => {
  const hasContent =
    isValidFieldAPI(sentiment) ||
    isValidFieldAPI(relevance) ||
    isValidFieldAPI(keyConnections) ||
    isValidFieldAPI(strategicImplications) ||
    isValidFieldAPI(competitiveIntelligence) ||
    isValidFieldAPI(partnershipOpportunities) ||
    isValidFieldAPI(riskFactors);

  if (!hasContent) return null;

  const orgName = organizationName || "Organization";

  const getSentimentStyle = (sentiment: string): React.CSSProperties => {
    const normalized = sentiment?.toLowerCase();
    switch (normalized) {
      case "positive":
        return {
          background: "#dcfce7",
          color: "#166534",
          border: "1px solid #bbf7d0",
        };
      case "negative":
        return {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fecaca",
        };
      case "neutral":
        return {
          background: "#f3f4f6",
          color: "#6b7280",
          border: "1px solid #d1d5db",
        };
      default:
        return {
          background: "#f3f4f6",
          color: "#6b7280",
          border: "1px solid #d1d5db",
        };
    }
  };

  const getRelevanceStyle = (relevance: string): React.CSSProperties => {
    const normalized = relevance?.toLowerCase();
    switch (normalized) {
      case "high":
        return {
          background: "#dbeafe",
          color: "#1e40af",
          border: "1px solid #bfdbfe",
        };
      case "medium":
        return {
          background: "#fef3c7",
          color: "#92400e",
          border: "1px solid #fde68a",
        };
      case "low":
        return {
          background: "#f3f4f6",
          color: "#6b7280",
          border: "1px solid #d1d5db",
        };
      default:
        return {
          background: "#f3f4f6",
          color: "#6b7280",
          border: "1px solid #d1d5db",
        };
    }
  };

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>🎯</span> Relevance for {orgName}
        </h4>
      </div>

      <div style={{ padding: "16px" }}>
        {(isValidFieldAPI(sentiment) || isValidFieldAPI(relevance)) && (
          <div
            style={{
              display: "flex",
              gap: "12px",
              marginBottom: "16px",
              flexWrap: "wrap",
            }}
          >
            {isValidFieldAPI(sentiment) && sentiment && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 12px",
                  borderRadius: "16px",
                  fontSize: "12px",
                  fontWeight: 500,
                  ...getSentimentStyle(sentiment),
                }}
              >
                <span>Sentiment:</span>
                <span style={{ fontWeight: 700 }}>{sentiment}</span>
              </div>
            )}
            {isValidFieldAPI(relevance) && relevance && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 12px",
                  borderRadius: "16px",
                  fontSize: "12px",
                  fontWeight: 500,
                  ...getRelevanceStyle(relevance),
                }}
              >
                <span>Relevance:</span>
                <span style={{ fontWeight: 700 }}>{relevance}</span>
              </div>
            )}
          </div>
        )}

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr", gap: "12px" }}
        >
          {isValidFieldAPI(strategicImplications) && (
            <div
              style={{
                padding: "12px",
                background: "#f8fafc",
                borderRadius: "8px",
                borderLeft: "4px solid #8b5cf6",
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                Strategic Implications
              </h5>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                {strategicImplications}
              </p>
            </div>
          )}

          {isValidFieldAPI(keyConnections) && (
            <div
              style={{
                padding: "12px",
                background: "#f8fafc",
                borderRadius: "8px",
                borderLeft: "4px solid #06b6d4",
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                Key Connections
              </h5>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                {keyConnections}
              </p>
            </div>
          )}

          {isValidFieldAPI(competitiveIntelligence) && (
            <div
              style={{
                padding: "12px",
                background: "#f8fafc",
                borderRadius: "8px",
                borderLeft: "4px solid #f59e0b",
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                Competitive Intelligence
              </h5>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                {competitiveIntelligence}
              </p>
            </div>
          )}

          {isValidFieldAPI(partnershipOpportunities) && (
            <div
              style={{
                padding: "12px",
                background: "#f0fdf4",
                borderRadius: "8px",
                borderLeft: "4px solid #16a34a",
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                Partnership Opportunities
              </h5>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                {partnershipOpportunities}
              </p>
            </div>
          )}

          {isValidFieldAPI(riskFactors) && (
            <div
              style={{
                padding: "12px",
                background: "#fef2f2",
                borderRadius: "8px",
                borderLeft: "4px solid #dc2626",
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                Risk Factors
              </h5>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                {riskFactors}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 🎯 KPI Metrics Section Component
export const KPIMetricsSection: React.FC<{
  newHires?: number;
  promotions?: number;
  retentionRate?: number;
  employeeGrowth?: number;
  totalEmployees?: number;
  executiveTurnover?: number;
}> = ({
  newHires,
  promotions,
  retentionRate,
  employeeGrowth,
  totalEmployees,
  executiveTurnover,
}) => {
  const hasContent =
    isValidFieldAPI(newHires) ||
    isValidFieldAPI(promotions) ||
    isValidFieldAPI(retentionRate) ||
    isValidFieldAPI(employeeGrowth) ||
    isValidFieldAPI(totalEmployees) ||
    isValidFieldAPI(executiveTurnover);

  if (!hasContent) return null;

  const getMetricColor = (value: any, context: string): string => {
    if (!isValidFieldAPI(value)) return "#6b7280";

    const numValue = typeof value === "string" ? parseFloat(value) : value;

    switch (context) {
      case "retention":
      case "growth":
        return numValue >= 0 ? "#16a34a" : "#dc2626";
      case "turnover":
        return numValue <= 10
          ? "#16a34a"
          : numValue <= 20
            ? "#d97706"
            : "#dc2626";
      default:
        return "#6b7280";
    }
  };

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>📊</span> KPI Metrics
        </h4>
      </div>

      <div style={{ padding: "16px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "12px",
          }}
        >
          {isValidFieldAPI(newHires) && (
            <div
              style={{
                padding: "12px",
                background: "#f8fafc",
                borderRadius: "8px",
                textAlign: "center",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  color: getMetricColor(newHires, "neutral"),
                  marginBottom: "4px",
                }}
              >
                {formatNumericAPI(newHires)}
              </div>
              <div
                style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}
              >
                New Hires
              </div>
            </div>
          )}

          {isValidFieldAPI(promotions) && (
            <div
              style={{
                padding: "12px",
                background: "#f8fafc",
                borderRadius: "8px",
                textAlign: "center",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  color: getMetricColor(promotions, "neutral"),
                  marginBottom: "4px",
                }}
              >
                {formatNumericAPI(promotions)}
              </div>
              <div
                style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}
              >
                Promotions
              </div>
            </div>
          )}

          {isValidFieldAPI(retentionRate) && (
            <div
              style={{
                padding: "12px",
                background: "#f8fafc",
                borderRadius: "8px",
                textAlign: "center",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  color: getMetricColor(retentionRate, "retention"),
                  marginBottom: "4px",
                }}
              >
                {formatNumericAPI(retentionRate)}%
              </div>
              <div
                style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}
              >
                Retention Rate
              </div>
            </div>
          )}

          {isValidFieldAPI(employeeGrowth) && (
            <div
              style={{
                padding: "12px",
                background: "#f8fafc",
                borderRadius: "8px",
                textAlign: "center",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  color: getMetricColor(employeeGrowth, "growth"),
                  marginBottom: "4px",
                }}
              >
                {formatNumericAPI(employeeGrowth)}%
              </div>
              <div
                style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}
              >
                Employee Growth
              </div>
            </div>
          )}

          {isValidFieldAPI(totalEmployees) && (
            <div
              style={{
                padding: "12px",
                background: "#f8fafc",
                borderRadius: "8px",
                textAlign: "center",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  color: getMetricColor(totalEmployees, "neutral"),
                  marginBottom: "4px",
                }}
              >
                {formatNumericAPI(totalEmployees)}
              </div>
              <div
                style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}
              >
                Total Employees
              </div>
            </div>
          )}

          {isValidFieldAPI(executiveTurnover) && (
            <div
              style={{
                padding: "12px",
                background: "#f8fafc",
                borderRadius: "8px",
                textAlign: "center",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  color: getMetricColor(executiveTurnover, "turnover"),
                  marginBottom: "4px",
                }}
              >
                {formatNumericAPI(executiveTurnover) === "N/A"
                  ? "Not Available"
                  : formatNumericAPI(executiveTurnover) + "%"}
              </div>
              <div
                style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}
              >
                Executive Turnover
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 🎯 Leadership Trends Section Component
export const LeadershipTrendsSection: React.FC<{
  fourWeekTrendDirection?: string;
  fourWeekTrendKeyChanges?: string;
  fourWeekTrendSignificance?: string;
  quarterlyKeyHires?: string;
  quarterlyExecutiveChanges?: string;
  quarterlyOrganizationalShifts?: string;
}> = ({
  fourWeekTrendDirection,
  fourWeekTrendKeyChanges,
  fourWeekTrendSignificance,
  quarterlyKeyHires,
  quarterlyExecutiveChanges,
  quarterlyOrganizationalShifts,
}) => {
  const hasFourWeekContent =
    isValidFieldAPI(fourWeekTrendDirection) ||
    isValidFieldAPI(fourWeekTrendKeyChanges) ||
    isValidFieldAPI(fourWeekTrendSignificance);
  const hasQuarterlyContent =
    isValidFieldAPI(quarterlyKeyHires) ||
    isValidFieldAPI(quarterlyExecutiveChanges) ||
    isValidFieldAPI(quarterlyOrganizationalShifts);

  if (!hasFourWeekContent && !hasQuarterlyContent) return null;

  const getTrendStyle = (trend: string): React.CSSProperties => {
    const normalized = trend?.toLowerCase();
    switch (normalized) {
      case "positive":
      case "up":
      case "increasing":
        return {
          background: "#dcfce7",
          color: "#166534",
          border: "1px solid #bbf7d0",
        };
      case "negative":
      case "down":
      case "decreasing":
        return {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fecaca",
        };
      case "stable":
      case "neutral":
      case "flat":
        return {
          background: "#dbeafe",
          color: "#1e40af",
          border: "1px solid #bfdbfe",
        };
      default:
        return {
          background: "#f3f4f6",
          color: "#6b7280",
          border: "1px solid #d1d5db",
        };
    }
  };

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>📈</span> Leadership Trends
        </h4>
      </div>

      <div style={{ padding: "16px" }}>
        {hasFourWeekContent && (
          <div style={{ marginBottom: hasQuarterlyContent ? "20px" : "0" }}>
            <h5
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                fontWeight: 600,
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>📅</span> 4-Week Trend
            </h5>
            {isValidFieldAPI(fourWeekTrendDirection) && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 12px",
                  borderRadius: "16px",
                  fontSize: "12px",
                  fontWeight: 500,
                  marginBottom: "12px",
                  ...(fourWeekTrendDirection
                    ? getTrendStyle(fourWeekTrendDirection)
                    : {}),
                }}
              >
                <span>Direction:</span>
                <span style={{ fontWeight: 700 }}>
                  {fourWeekTrendDirection}
                </span>
              </div>
            )}
            {isValidFieldAPI(fourWeekTrendKeyChanges) && (
              <div
                style={{
                  padding: "12px",
                  background: "#f8fafc",
                  borderRadius: "8px",
                  marginBottom: "8px",
                }}
              >
                <h6
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  Key Changes
                </h6>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#6b7280",
                    lineHeight: 1.5,
                  }}
                >
                  {fourWeekTrendKeyChanges}
                </p>
              </div>
            )}
            {isValidFieldAPI(fourWeekTrendSignificance) && (
              <div
                style={{
                  padding: "12px",
                  background: "#f8fafc",
                  borderRadius: "8px",
                }}
              >
                <h6
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  Significance
                </h6>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#6b7280",
                    lineHeight: 1.5,
                  }}
                >
                  {fourWeekTrendSignificance}
                </p>
              </div>
            )}
          </div>
        )}

        {hasQuarterlyContent && (
          <div>
            <h5
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                fontWeight: 600,
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>📊</span> Quarterly Comparison
            </h5>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "12px",
              }}
            >
              {isValidFieldAPI(quarterlyKeyHires) && (
                <div
                  style={{
                    padding: "12px",
                    background: "#f0fdf4",
                    borderRadius: "8px",
                    borderLeft: "4px solid #16a34a",
                  }}
                >
                  <h6
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Key Hires
                  </h6>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      color: "#6b7280",
                      lineHeight: 1.5,
                    }}
                  >
                    {quarterlyKeyHires}
                  </p>
                </div>
              )}

              {isValidFieldAPI(quarterlyExecutiveChanges) && (
                <div
                  style={{
                    padding: "12px",
                    background: "#fef3c7",
                    borderRadius: "8px",
                    borderLeft: "4px solid #d97706",
                  }}
                >
                  <h6
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Executive Changes
                  </h6>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      color: "#6b7280",
                      lineHeight: 1.5,
                    }}
                  >
                    {quarterlyExecutiveChanges}
                  </p>
                </div>
              )}

              {isValidFieldAPI(quarterlyOrganizationalShifts) && (
                <div
                  style={{
                    padding: "12px",
                    background: "#ede9fe",
                    borderRadius: "8px",
                    borderLeft: "4px solid #8b5cf6",
                  }}
                >
                  <h6
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Organizational Shifts
                  </h6>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      color: "#6b7280",
                      lineHeight: 1.5,
                    }}
                  >
                    {quarterlyOrganizationalShifts}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 🎯 Risk Assessment Section Component
export const RiskAssessmentSection: React.FC<{
  culturalRisk?: string;
  keyPersonRisk?: string;
  overallStability?: string;
  successionPlanning?: string;
}> = ({
  culturalRisk,
  keyPersonRisk,
  overallStability,
  successionPlanning,
}) => {
  const hasContent =
    isValidFieldAPI(culturalRisk) ||
    isValidFieldAPI(keyPersonRisk) ||
    isValidFieldAPI(overallStability) ||
    isValidFieldAPI(successionPlanning);

  if (!hasContent) return null;

  const getRiskStyle = (risk: string): React.CSSProperties => {
    const normalized = risk?.toLowerCase();
    if (normalized.includes("low")) {
      return {
        background: "#dcfce7",
        color: "#166534",
        border: "1px solid #bbf7d0",
      };
    }
    if (normalized.includes("medium") || normalized.includes("moderate")) {
      return {
        background: "#fef3c7",
        color: "#92400e",
        border: "1px solid #fde68a",
      };
    }
    if (normalized.includes("high")) {
      return {
        background: "#fee2e2",
        color: "#991b1b",
        border: "1px solid #fecaca",
      };
    }
    return {
      background: "#f3f4f6",
      color: "#6b7280",
      border: "1px solid #d1d5db",
    };
  };

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>⚠️</span> Risk Assessment
        </h4>
      </div>

      <div style={{ padding: "16px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "16px",
          }}
        >
          {isValidFieldAPI(culturalRisk) && culturalRisk && (
            <div
              style={{
                padding: "12px",
                borderRadius: "8px",
                ...getRiskStyle(culturalRisk),
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                Cultural Risk
              </h5>
              <p style={{ margin: 0, fontSize: "13px", lineHeight: 1.4 }}>
                {culturalRisk}
              </p>
            </div>
          )}

          {isValidFieldAPI(keyPersonRisk) && keyPersonRisk && (
            <div
              style={{
                padding: "12px",
                borderRadius: "8px",
                ...getRiskStyle(keyPersonRisk),
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                Key Person Risk
              </h5>
              <p style={{ margin: 0, fontSize: "13px", lineHeight: 1.4 }}>
                {keyPersonRisk}
              </p>
            </div>
          )}

          {isValidFieldAPI(overallStability) && overallStability && (
            <div
              style={{
                padding: "12px",
                borderRadius: "8px",
                ...getRiskStyle(overallStability),
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                Overall Stability
              </h5>
              <p style={{ margin: 0, fontSize: "13px", lineHeight: 1.4 }}>
                {overallStability}
              </p>
            </div>
          )}

          {isValidFieldAPI(successionPlanning) && successionPlanning && (
            <div
              style={{
                padding: "12px",
                borderRadius: "8px",
                ...getRiskStyle(successionPlanning),
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                Succession Planning
              </h5>
              <p style={{ margin: 0, fontSize: "13px", lineHeight: 1.4 }}>
                {successionPlanning}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 🎯 Top Leadership Team Section Component
export const TopLeadershipTeamSection: React.FC<{
  top10Execs?: Executive[];
}> = ({ top10Execs }) => {
  if (!Array.isArray(top10Execs) || top10Execs.length === 0) return null;

  const validExecs = top10Execs
    .filter(
      exec =>
        exec && (isValidFieldAPI(exec.name) || isValidFieldAPI(exec.title))
    )
    .slice(0, 10);

  if (validExecs.length === 0) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>👔</span> Top Leadership Team
        </h4>
      </div>

      <div style={{ padding: "16px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "16px",
          }}
        >
          {validExecs.map((exec, index) => (
            <div
              key={index}
              style={{
                padding: "16px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
              }}
            >
              {isValidFieldAPI(exec.name) && (
                <h5
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  {exec.name}
                </h5>
              )}
              {isValidFieldAPI(exec.title) && (
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "13px",
                    color: "#8b5cf6",
                    fontWeight: 500,
                  }}
                >
                  {exec.title}
                </p>
              )}
              {isValidFieldAPI(exec.role) && (
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "12px",
                    color: "#64748b",
                  }}
                >
                  {exec.role}
                </p>
              )}
              {isValidFieldAPI(exec.briefBio) && (
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "12px",
                    color: "#6b7280",
                    lineHeight: 1.4,
                  }}
                >
                  {exec.briefBio}
                </p>
              )}
              {isValidFieldAPI(exec.tenure) && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#64748b",
                    marginTop: "8px",
                  }}
                >
                  <span>Tenure: {exec.tenure}</span>
                </div>
              )}
              {isValidFieldAPI(exec.source) && (
                <div style={{ marginTop: "8px" }}>
                  <a
                    href={exec.source}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: "11px",
                      color: "#3b82f6",
                      textDecoration: "none",
                    }}
                  >
                    View Details →
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// 🎯 Executive & Board Changes Section Component
export const ExecutiveBoardChangesSection: React.FC<{
  executiveChanges?: ExecutiveChange[];
  boardChanges?: ExecutiveChange[];
}> = ({ executiveChanges, boardChanges }) => {
  const validExecutiveChanges = Array.isArray(executiveChanges)
    ? executiveChanges
        .filter(
          change => change && isValidFieldAPI(change.name || change.title)
        )
        .slice(0, 5)
    : [];

  const validBoardChanges = Array.isArray(boardChanges)
    ? boardChanges
        .filter(
          change => change && isValidFieldAPI(change.name || change.title)
        )
        .slice(0, 5)
    : [];

  if (validExecutiveChanges.length === 0 && validBoardChanges.length === 0)
    return null;

  const getChangeTypeStyle = (type: string): React.CSSProperties => {
    const normalized = type?.toLowerCase();
    switch (normalized) {
      case "hire":
      case "appointment":
        return {
          background: "#dcfce7",
          color: "#166534",
          border: "1px solid #bbf7d0",
        };
      case "promotion":
        return {
          background: "#dbeafe",
          color: "#1e40af",
          border: "1px solid #bfdbfe",
        };
      case "departure":
      case "resignation":
        return {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fecaca",
        };
      case "board_appointment":
        return {
          background: "#ede9fe",
          color: "#7c3aed",
          border: "1px solid #c4b5fd",
        };
      default:
        return {
          background: "#f3f4f6",
          color: "#6b7280",
          border: "1px solid #d1d5db",
        };
    }
  };

  const ChangeItem: React.FC<{ change: ExecutiveChange }> = ({ change }) => (
    <div
      style={{
        padding: "12px",
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        marginBottom: "12px",
      }}
    >
      {isValidFieldAPI(change.name) && (
        <h6
          style={{
            margin: "0 0 8px 0",
            fontSize: "14px",
            fontWeight: 600,
            color: "#374151",
          }}
        >
          {change.name}
        </h6>
      )}
      {isValidFieldAPI(change.title) && (
        <p
          style={{
            margin: "0 0 8px 0",
            fontSize: "13px",
            color: "#8b5cf6",
            fontWeight: 500,
          }}
        >
          {change.title}
        </p>
      )}
      {isValidFieldAPI(change.type) && change.type && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "2px 8px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: 500,
            marginBottom: "8px",
            ...getChangeTypeStyle(change.type),
          }}
        >
          {change.type}
        </div>
      )}
      {isValidFieldAPI(change.description) && (
        <p
          style={{
            margin: "0 0 8px 0",
            fontSize: "12px",
            color: "#6b7280",
            lineHeight: 1.4,
          }}
        >
          {change.description}
        </p>
      )}
      {isValidFieldAPI(change.effectiveDate) && (
        <div style={{ fontSize: "11px", color: "#64748b" }}>
          <span>Effective: {formatDateAPI(change.effectiveDate)}</span>
        </div>
      )}
    </div>
  );

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>🔄</span> Executive & Board Changes
        </h4>
      </div>

      <div style={{ padding: "16px" }}>
        {validExecutiveChanges.length > 0 && (
          <div
            style={{
              marginBottom: validBoardChanges.length > 0 ? "20px" : "0",
            }}
          >
            <h5
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                fontWeight: 600,
                color: "#374151",
              }}
            >
              Executive Changes
            </h5>
            {validExecutiveChanges.map((change, index) => (
              <ChangeItem key={`exec-${index}`} change={change} />
            ))}
          </div>
        )}

        {validBoardChanges.length > 0 && (
          <div>
            <h5
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                fontWeight: 600,
                color: "#374151",
              }}
            >
              Board Changes
            </h5>
            {validBoardChanges.map((change, index) => (
              <ChangeItem key={`board-${index}`} change={change} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// 🎯 Organizational Changes Section Component
export const OrganizationalChangesSection: React.FC<{
  organizationalChanges?: OrganizationalChange[];
}> = ({ organizationalChanges }) => {
  if (
    !Array.isArray(organizationalChanges) ||
    organizationalChanges.length === 0
  )
    return null;

  const validChanges = organizationalChanges
    .filter(
      change => change && isValidFieldAPI(change.title || change.description)
    )
    .slice(0, 8);

  if (validChanges.length === 0) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>🏢</span> Organizational Changes
        </h4>
      </div>

      <div style={{ padding: "16px" }}>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr", gap: "12px" }}
        >
          {validChanges.map((change, index) => (
            <div
              key={index}
              style={{
                padding: "12px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
              }}
            >
              {isValidFieldAPI(change.title) && (
                <h6
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  {change.title}
                </h6>
              )}
              {isValidFieldAPI(change.description) && (
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "13px",
                    color: "#6b7280",
                    lineHeight: 1.4,
                  }}
                >
                  {change.description}
                </p>
              )}
              {isValidFieldAPI(change.impact) && (
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "12px",
                    color: "#8b5cf6",
                    fontStyle: "italic",
                  }}
                >
                  Impact: {change.impact}
                </p>
              )}
              {isValidFieldAPI(change.effectiveDate) && (
                <div style={{ fontSize: "11px", color: "#64748b" }}>
                  <span>Effective: {formatDateAPI(change.effectiveDate)}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// 🎯 Cultural Initiatives Section Component
export const CulturalInitiativesSection: React.FC<{
  culturalInitiatives?: CulturalInitiative[];
}> = ({ culturalInitiatives }) => {
  if (!Array.isArray(culturalInitiatives) || culturalInitiatives.length === 0)
    return null;

  const validInitiatives = culturalInitiatives
    .filter(
      initiative =>
        initiative &&
        isValidFieldAPI(initiative.title || initiative.description)
    )
    .slice(0, 6);

  if (validInitiatives.length === 0) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>🌟</span> Cultural Initiatives
        </h4>
      </div>

      <div style={{ padding: "16px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "12px",
          }}
        >
          {validInitiatives.map((initiative, index) => (
            <div
              key={index}
              style={{
                padding: "12px",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "8px",
              }}
            >
              {isValidFieldAPI(initiative.title) && (
                <h6
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  {initiative.title}
                </h6>
              )}
              {isValidFieldAPI(initiative.description) && (
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "13px",
                    color: "#6b7280",
                    lineHeight: 1.4,
                  }}
                >
                  {initiative.description}
                </p>
              )}
              {isValidFieldAPI(initiative.status) && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#16a34a",
                    fontWeight: 500,
                  }}
                >
                  <span>Status: {initiative.status}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
